
import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from '@/hooks/useAuth';
import { toast } from "sonner";
import { LogIn, UserPlus, ExternalLink } from 'lucide-react';
import { Separator } from "@/components/ui/separator";
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

interface AuthFormProps {
  initialEmail?: string | null;
}

const AuthForm = ({ initialEmail }: AuthFormProps) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('signin');
  const { signIn, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  
  useEffect(() => {
    if (initialEmail) {
      setEmail(initialEmail);
      setActiveTab('signup');
    }
  }, [initialEmail]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please enter both email and password");
      return;
    }
    
    setIsSubmitting(true);
    try {
      await signIn(email, password);
    } catch (error) {
      console.error('Error signing in:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please enter both email and password");
      return;
    }
    
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setIsSubmitting(true);
    try {
      // Add user to waitlist instead of creating account
      const { error: waitlistError } = await supabase
        .from('waitlist')
        .insert([
          {
            email: email,
            signup_method: 'email'
          }
        ]);

      if (waitlistError) {
        if (waitlistError.code === '23505') { // Unique constraint violation
          toast.error("You're already on the waitlist!");
        } else {
          throw waitlistError;
        }
      } else {
        toast.success("Successfully joined the waitlist!");
        navigate('/waitlist?success=true');
      }
    } catch (error: any) {
      console.error('Error joining waitlist:', error);
      toast.error('Failed to join waitlist. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      console.log('Initiating Google Sign-in flow for waitlist');
      toast.info('Redirecting to Google...');
      
      const { error } = await supabase.auth.signInWithOAuth({ 
        provider: 'google',
        options: {
          redirectTo: window.location.origin + '/waitlist?google_signup=true',
          queryParams: {
            prompt: 'select_account',
            access_type: 'offline'
          }
        }
      });
      
      if (error) throw error;
    } catch (error) {
      console.error('Error signing in with Google:', error);
      toast.error('Google sign-in failed. Please try again.');
    }
  };

  return (
    <div className="max-w-md w-full glass-card p-8 rounded-xl">
      <Tabs defaultValue={activeTab} value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-8">
          <TabsTrigger value="signin">Sign In</TabsTrigger>
          <TabsTrigger value="signup">Join Waitlist</TabsTrigger>
        </TabsList>

        <TabsContent value="signin">
          <form onSubmit={handleSignIn} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email-signin">Email</Label>
              <Input
                id="email-signin"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-white/5 border-white/10 focus:border-[#9E7C47]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password-signin">Password</Label>
              <Input
                id="password-signin"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-white/5 border-white/10 focus:border-[#9E7C47]"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-[#9E7C47] hover:bg-[#9E7C47]/90 font-semibold text-white py-3"
              disabled={isSubmitting}
              size="lg"
            >
              {isSubmitting ? "Signing in..." : (
                <>
                  <LogIn className="h-5 w-5 mr-2" />
                  Sign In
                </>
              )}
            </Button>
            
            <div className="relative my-6">
              <Separator className="absolute inset-0 flex items-center" aria-hidden="true">
                <span className="w-full border-t border-white/10"></span>
              </Separator>
              <div className="relative flex justify-center">
                <span className="bg-black px-4 text-sm text-white/50">or continue with</span>
              </div>
            </div>
            
            <Button
              type="button"
              variant="outline"
              className="w-full border-white/10 text-white hover:bg-white/10 font-medium"
              onClick={handleGoogleSignIn}
              size="lg"
            >
              <ExternalLink className="h-5 w-5 mr-2" />
              Sign in with Google
            </Button>
          </form>
        </TabsContent>

        <TabsContent value="signup">
          <form onSubmit={handleSignUp} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email-signup">Email</Label>
              <Input
                id="email-signup"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-white/5 border-white/10 focus:border-[#9E7C47]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password-signup">Password</Label>
              <Input
                id="password-signup"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-white/5 border-white/10 focus:border-[#9E7C47]"
                minLength={6}
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-[#9E7C47] hover:bg-[#9E7C47]/90 font-semibold text-white py-3"
              disabled={isSubmitting}
              size="lg"
            >
              {isSubmitting ? "Requesting access..." : (
                <>
                  <UserPlus className="h-5 w-5 mr-2" />
                  Request Early Access
                </>
              )}
            </Button>
            
            <div className="relative my-6">
              <Separator className="absolute inset-0 flex items-center" aria-hidden="true">
                <span className="w-full border-t border-white/10"></span>
              </Separator>
              <div className="relative flex justify-center">
                <span className="bg-black px-4 text-sm text-white/50">or continue with</span>
              </div>
            </div>
            
            <Button
              type="button"
              variant="outline"
              className="w-full border-white/10 text-white hover:bg-white/10 font-medium"
              onClick={handleGoogleSignIn}
              size="lg"
            >
              <ExternalLink className="h-5 w-5 mr-2" />
              Join with Google - Invitation Only
            </Button>
            
            <p className="text-xs text-white/50 text-center mt-4">
              By requesting access, you agree to our Terms of Service and Privacy Policy.
              <br />
              <span className="text-[#9E7C47]">Limited to first 1000 founding members!</span>
            </p>
          </form>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AuthForm;
